import Vue from 'vue';
import AllSportsService from '../../services/allsports-service';
import SportModel from '../../models/model-sport';
import SportsComboDataRequestService from '../../services/sports-combo-data-request-service';
import SportsMarketsUs from '../../services/sports-markets-us';
import SportTabsRequestService from '../../components/widgets/category/service-sport-tabs';
import MarketModel from '../../components/widgets/category/model-market';
import GetLiveMatchTabs from '@bto-services/api/shared/get-live-match-tabs-service';

/**
 * Vuex store - sports module
 */
export const SportsModule = {
  state: {
    /**
     * This state property will contain all available sports
     * so we can use it for icon classes wherever is needed
     */
    sport: {},
    allSportsList: [],
    allSportsData: [],
    sportDataRecevied: false,
    sportMarkets: {},
    sportCombos: {},
    sportCombosUs: {},
    selectedMarket: {},
    categoryMap: {},
    hrSportId: 0,
    ghSportId: 0,
    asyncFixtureFlag: false,
    liveMarketGroupsTabs: {}
  },
  mutations: {
    /**
     * Update all sports data
     * @param state
     * @param data
     */
    updateAllSportData(state, data) {
      Vue.set(state, 'allSportsData', data);
    },
    /**
     * Checks wheater sport data request has started
     * @param state
     * @param payload
     */
    updateSelectedSport(state, sportId) {
      if (!sportId) {
        return;
      }
      let sportSelected = false;
      for (let i = 0; i < state.allSportsList.length; i += 1) {
        if (state.allSportsList[i].id === sportId) {
          state.sport = state.allSportsList[i];
          sportSelected = true;
          break;
        }
      }
      // after updated sports list, if selected sport is not found, reset selected sport
      if (!sportSelected) {
        state.sport = {};
      }
    },
    setDataReceivedFlag(state, payload) {
      state.sportDataRecevied = payload;
    },
    saveSportMarkets(state, payload) {
      Vue.set(state.sportMarkets, payload.sportId, payload.markets);
    },
    setSelectedMarket(state, payload) {
      state.selectedMarket = Object.assign({}, payload);
    },
    saveSportCombos(state, payload) {
      state.sportCombos = payload;
    },
    saveSportCombosUs(state, payload) {
      state.sportCombosUs = payload;
    },
    saveLiveMatchTabs(state, payload) {
      state.liveMarketGroupsTabs = payload
    },
    updateAllSportsList(state, sports) {
      state.allSportsList = sports;
    },
    setCategoryMap(state, catMap) {
      state.categoryMap = catMap;
    },
    updateAsyncFixtureFlag(state, flag) {
      state.asyncFixtureFlag = flag;
    },
    setHrSportId(state) {
      const horseSport = Object.values(state.allSportsData).find(t => t.class === 'horse-racing' && t.type === 3);
      if (horseSport) {
        state.hrSportId = horseSport.id;
      }
    },
    setGhSportId(state) {
      const greyhoundSport = Object.values(state.allSportsData).find(t => t.class === 'greyhound' && t.type === 3);
      if (greyhoundSport) {
        state.ghSportId = greyhoundSport.id;
      }
    }
  },
  actions: {
    getMarkets({ commit, state }, sid) {
      // markets for sport already exist
      if (state.sportMarkets[sid]) {
        Vue.set(state.sportMarkets, sid, state.sportMarkets[sid]);
        return;
      }
      let sportTabsRequest = new SportTabsRequestService();
      sportTabsRequest.getParams.sportID = sid;
      sportTabsRequest.execute((response) => {
        let markets = [];
        if (response.data) {
          markets = response.data.t.map(m => new MarketModel(m));
        }
        commit('saveSportMarkets', { sportId: sid, markets: markets });
      }, (error) => {
        console.error(error); // eslint-disable-line
      });
    },
    getSportComboData({ commit }) {
      let sportComboRequest = new SportsComboDataRequestService();
      sportComboRequest.execute((response) => {
        commit('saveSportCombos', response.data);
      });
    },
    getSportComboDataUs({ commit }) {
      let sportComboRequest = new SportsMarketsUs();
      sportComboRequest.execute((response) => {
        commit('saveSportCombosUs', response.data);
      });
    },
    getLiveMatchTabs({ commit }) {
      let getLiveMatchTabs = new GetLiveMatchTabs();
      getLiveMatchTabs.execute((resp) => {
        Object.values(resp.data).forEach(mtg => {
          mtg.sort((a, b) => a.Order - b.Order);
        });
        commit('saveLiveMatchTabs', resp.data)
      })
    },
    /**
     * Get all sports data
     * @param commit
     * @param state
     */
    getAllSportData({ commit, state }) {
      if (state.sportDataRecevied) {
        return;
      }
      // initialize new request service
      let allSportsReq = new AllSportsService();
      commit('setDataReceivedFlag', true);
      allSportsReq.execute((resp) => {
        // update store property with received api data
        if (resp.data instanceof Array && resp.data.length > 0) {
          let respObj = resp.data.reduce((s, curr) => {
            s[curr.id] = new SportModel(curr);
            return s;
          }, {});
          commit('updateAllSportData', respObj);
          if (!state.hrSportId) {
            commit('setHrSportId');
          }
          if (!state.ghSportId) {
            commit('setGhSportId');
          }
        }
      }, () => {
        commit('setDataReceivedFlag', false);
      });
    },
    getSportType({ state }, sportId) {
      return typeof state.allSportsData[sportId] !== 'undefined' ? state.allSportsData[sportId].type : '';
    },
  },
  getters: {
    /**
     * Get sport icon class
     * @param state
     * @returns {Function}
     */
    getSportIconClass(state) {
      return sportId => (typeof state.allSportsData[sportId] !== 'undefined' ? state.allSportsData[sportId].class : '');
    },
    getSportType(state) {
      return sportId => (typeof state.allSportsData[sportId] !== 'undefined' ? state.allSportsData[sportId].type : '');
    },
    /**
     * Get sport data by sport id
     * @param state
     * @returns {Function}
     */
    getSportData(state) {
      return (sportId = '') => {
        if (!sportId) {
          return {};
        }
        if (typeof state.allSportsData[sportId] !== 'undefined') {
          return state.allSportsData[sportId];
        }
        return {};
      };
    },
    getSportMarkets(state) {
      return sportId => {
        return state.sportMarkets[sportId];
      };
    },
    getLiveMatchGroupTabs(state) {
      return sportId => {
        return state.liveMarketGroupsTabs[sportId] || [];
      };
    },
    getSportMarketsUs(state) {
      return sportId => {
        return state.sportCombosUs[sportId] ? Object.values(state.sportCombosUs[sportId]) : [];
      };
    },
    getSportClass(state) {
      return sportId => {
        let sport = state.allSportsData[sportId];
        return sport ? sport.class : '';
      };
    },
    getSportName(state) {
      return sportId => {
        let sport = state.allSportsData[sportId];
        return sport ? sport.name : '';
      };
    },
  },
};
